import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { PaperPlaneRight, ArrowsClockwise, UserCircle, Robot, Headset, ThumbsUp, ThumbsDown, CheckCircle, LinkSimple, BookOpen, Brain, Lightning, CircleNotch, Paperclip, X, Star, SignOut, Info, LockKey, FileText } from "@phosphor-icons/react";
import { streamChat, avatarSrc, api } from "@/lib/api";

/**
 * Återanvändbar strömmande chattkonsol.
 * Exponerar runQueue(questions) och send(text) via ref.
 */
const ChatPanel = forwardRef(function ChatPanel({ chatPath, agent, onQueueDone, proactiveRef }, ref) {
  const color = agent?.primary_color || "#0033FF";
  const avatar = avatarSrc(agent);
  const visionEnabled = agent?.vision_enabled !== false && agent?.type !== "voice";
  const fileUploadsEnabled = agent?.file_uploads_enabled !== false && agent?.type !== "voice";
  const feedbackPath = chatPath.replace(/\/chat$/, "/feedback");
  const surveyPath = chatPath.replace(/\/chat(\?|$)/, "/csat-survey$1");
  const surveyEnabled = agent?.csat_survey_enabled !== false && chatPath.includes("/public/");
  const npsEnabled = !!agent?.nps_enabled;
  const showDisclosure = agent?.ai_disclosure_enabled !== false && chatPath.includes("/public/")
    && (agent?.type !== "voice");
  const disclosureText = agent?.ai_disclosure_text || "Du chattar med en AI-assistent.";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [humanMode, setHumanMode] = useState(false);
  const [supportName, setSupportName] = useState("");
  const [feedback, setFeedback] = useState({});
  const [pendingImage, setPendingImage] = useState(null); // {dataUrl, base64}
  const [pendingFile, setPendingFile] = useState(null); // {name, text}
  const [fileBusy, setFileBusy] = useState(false);
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyDone, setSurveyDone] = useState(false);
  const [surveyCsat, setSurveyCsat] = useState(0);
  const [surveyNps, setSurveyNps] = useState(null);
  const [surveyComment, setSurveyComment] = useState("");
  const [surveyBusy, setSurveyBusy] = useState(false);
  const accessProtected = !!agent?.access_protected && chatPath.includes("/public/");
  const [accessOk, setAccessOk] = useState(!accessProtected);
  const [accessInput, setAccessInput] = useState("");
  const [accessError, setAccessError] = useState("");
  const accessCodeRef = useRef("");
  const fileRef = useRef(null);
  const sessionRef = useRef(`sess-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);
  const queueRef = useRef([]);
  const runningQueueRef = useRef(false);
  const humanModeRef = useRef(false);
  const agentShownRef = useRef(0);
  const wsRef = useRef(null);
  const syncRef = useRef(null);
  const typingTimerRef = useRef(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const supportsLive = chatPath.includes("/public/");
  const messagesPath = chatPath.replace(/\/chat(\?|$)/, "/messages$1");
  const uploadPath = chatPath.replace(/\/chat(\?|$)/, "/upload$1");

  const enterHumanMode = () => {
    if (!supportsLive) return;
    humanModeRef.current = true;
    setHumanMode(true);
    setEscalated(true);
  };

  // Live human handover: once the AI hands off, poll for the human agent's replies.
  useEffect(() => {
    if (!humanMode || !supportsLive) return;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api.get(messagesPath, { params: { session_id: sessionRef.current } });
        const d = r.data || {};
        if (d.assigned_agent_name) setSupportName(d.assigned_agent_name);
        const agentMsgs = (d.messages || []).filter((m) => m.role === "agent");
        if (agentMsgs.length > agentShownRef.current) {
          const fresh = agentMsgs.slice(agentShownRef.current);
          agentShownRef.current = agentMsgs.length;
          setMessages((m) => [...m, ...fresh.map((x) => ({ role: "agent", content: x.content }))]);
          setAgentTyping(false);
        }
        if (d.status === "resolved") { humanModeRef.current = false; setHumanMode(false); }
      } catch { /* keep polling */ }
    };
    syncRef.current = poll;
    poll();
    // WebSocket gives instant updates; polling stays as a slow fallback.
    const id = setInterval(() => { if (!stopped) poll(); }, 12000);
    return () => { stopped = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanMode, supportsLive, messagesPath]);

  // Realtime widget websocket: instant agent replies, status + typing.
  useEffect(() => {
    if (!humanMode || !supportsLive) return;
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    let stop = false, retry = null, ws = null;
    const connect = () => {
      if (stop) return;
      try { ws = new WebSocket(`${base}/api/ws/widget/${agent?.id}/${sessionRef.current}`); } catch { return; }
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.type === "message" || d.type === "status") {
            if (d.assigned_agent_name) setSupportName(d.assigned_agent_name);
            if (d.status === "resolved") { humanModeRef.current = false; setHumanMode(false); }
            syncRef.current?.();
          } else if (d.type === "typing" && d.who === "agent") {
            setAgentTyping(!!d.on);
          }
        } catch { /* ignore */ }
      };
      ws.onclose = () => { wsRef.current = null; if (!stop) retry = setTimeout(connect, 3000); };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
    };
    connect();
    return () => { stop = true; clearTimeout(retry); try { ws && ws.close(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanMode, supportsLive, agent?.id]);

  useEffect(() => {
    if (agent?.welcome_message) setMessages([{ role: "assistant", content: agent.welcome_message }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const reset = () => {
    sessionRef.current = `sess-${Math.random().toString(36).slice(2)}`;
    setEscalated(false);
    setFeedback({});
    humanModeRef.current = false;
    setHumanMode(false);
    setSupportName("");
    setAgentTyping(false);
    agentShownRef.current = 0;
    setSurveyOpen(false);
    setSurveyDone(false);
    setSurveyCsat(0);
    setSurveyNps(null);
    setSurveyComment("");
    setPendingImage(null);
    setPendingFile(null);
    setMessages(agent?.welcome_message ? [{ role: "assistant", content: agent.welcome_message }] : []);
  };

  const hasUserMessage = messages.some((m) => m.role === "user");

  const unlockAccess = () => {
    if (!accessInput.trim()) return;
    accessCodeRef.current = accessInput.trim();
    setAccessOk(true);
    setAccessError("");
  };

  const submitSurvey = async () => {
    if (!surveyCsat && surveyNps === null) return;
    setSurveyBusy(true);
    try {
      await api.post(surveyPath, {
        session_id: sessionRef.current,
        csat: surveyCsat || null,
        nps: surveyNps,
        comment: surveyComment.trim(),
      });
      setSurveyDone(true);
    } catch (e) { /* non-blocking */ setSurveyDone(true); }
    finally { setSurveyBusy(false); }
  };

  const sendFeedback = async (index, rating) => {
    const assistantMessage = messages[index]?.content || "";
    let userMessage = "";
    for (let j = index - 1; j >= 0; j--) {
      if (messages[j].role === "user") { userMessage = messages[j].content; break; }
    }
    setFeedback((f) => ({ ...f, [index]: rating }));
    try {
      await api.post(feedbackPath, {
        session_id: sessionRef.current,
        user_message: userMessage,
        assistant_message: assistantMessage,
        rating,
      });
    } catch (e) { /* non-blocking */ }
  };

  const sendText = async (text, image, file) => {
    if (!text && !image && !file) return;
    const human = humanModeRef.current;
    setBusy(true);
    const bubble = { role: "user", content: text, image: image?.dataUrl, file: file?.name };
    if (human) {
      setMessages((m) => [...m, bubble]);
    } else {
      setMessages((m) => [...m, bubble, { role: "assistant", content: "" }]);
    }
    let outgoing = text || "";
    if (file?.text) {
      outgoing = `${outgoing}\n\n[Kunden har bifogat filen "${file.name}". Filens innehåll:\n${file.text}\n--- slut på fil ---]`.trim();
    }
    await streamChat({
      path: chatPath,
      message: outgoing,
      sessionId: sessionRef.current,
      imageBase64: image?.base64,
      ref: proactiveRef,
      accessCode: accessCodeRef.current,
      onDelta: (d) => {
        if (human) return;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + d, steps: copy[copy.length - 1].steps };
          return copy;
        });
      },
      onStep: (s) => {
        if (human) return;
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          copy[copy.length - 1] = { ...last, steps: [...(last.steps || []), s] };
          return copy;
        });
      },
      onDone: (p) => {
        if (p.escalated || p.human) enterHumanMode();
        if (human) { setBusy(false); return; }
        if (p.blocked && p.reply != null) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: p.reply, sources: [], confidence: p.confidence, actions: [], blocked: p.blocked };
            return copy;
          });
        } else if ((p.sources && p.sources.length) || p.confidence != null || (p.actions && p.actions.length)) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, ...(p.reply != null ? { content: p.reply } : {}), sources: p.sources || [], confidence: p.confidence, actions: p.actions || [] };
            return copy;
          });
        } else if (p.reply != null) {
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: p.reply };
            return copy;
          });
        }
        setBusy(false);
        if (queueRef.current.length) {
          const next = queueRef.current.shift();
          setTimeout(() => sendText(next), 900);
        } else if (runningQueueRef.current) {
          runningQueueRef.current = false;
          onQueueDone?.();
        }
      },
      onError: (e) => {
        if (e?.code === 403) {
          setAccessOk(false);
          setAccessError("Fel åtkomstkod. Försök igen.");
          accessCodeRef.current = "";
          setMessages((m) => m.slice(0, human ? -1 : -2));
          setBusy(false);
          return;
        }
        if (!human) {
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: "⚠️ Något gick fel. Försök igen." };
            return copy;
          });
        }
        setBusy(false);
      },
    });
  };

  useImperativeHandle(ref, () => ({
    runQueue: (questions) => {
      const list = (questions || []).filter(Boolean);
      if (!list.length) return;
      reset();
      runningQueueRef.current = true;
      queueRef.current = [...list];
      setTimeout(() => {
        const first = queueRef.current.shift();
        sendText(first);
      }, 500);
    },
    send: (t) => !busy && sendText(t),
  }));

  const send = (e) => {
    e?.preventDefault();
    const text = input.trim();
    if ((!text && !pendingImage && !pendingFile) || busy || fileBusy) return;
    setInput("");
    const img = pendingImage;
    const file = pendingFile;
    setPendingImage(null);
    setPendingFile(null);
    sendText(text, img, file);
  };

  const pickAttachment = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type.startsWith("image/")) {
      if (!visionEnabled) return;
      return pickImageFile(file);
    }
    // document upload → extract text on the server
    if (!fileUploadsEnabled) return;
    if (file.size > 10 * 1024 * 1024) { alert("Filen är för stor (max 10 MB)."); return; }
    const fd = new FormData();
    fd.append("file", file);
    setFileBusy(true);
    try {
      const { data } = await api.post(uploadPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingFile({ name: data.filename, text: data.text });
    } catch (err) {
      alert(err?.response?.data?.detail || "Kunde inte läsa filen.");
    } finally {
      setFileBusy(false);
    }
  };

  const pickImageFile = async (file) => {
    if (!file.type.startsWith("image/")) return;
    if (file.size > 15 * 1024 * 1024) { alert("Bilden är för stor (max 15 MB)."); return; }
    // Downscale to max 1024px and re-encode as JPEG to keep payload small.
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const max = 1024;
          let { width, height } = img;
          if (width > max || height > max) {
            const r = Math.min(max / width, max / height);
            width = Math.round(width * r);
            height = Math.round(height * r);
          }
          const canvas = document.createElement("canvas");
          canvas.width = width; canvas.height = height;
          canvas.getContext("2d").drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.85));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    setPendingImage({ dataUrl, base64: dataUrl.split(",")[1] });
  };

  const AvatarIcon = ({ size = 18 }) =>
    avatar ? <img src={avatar} alt="" className="h-full w-full object-cover rounded-full" /> : <Robot size={size} weight="fill" />;

  return (
    <div className="flex flex-col h-full bg-white relative">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200" style={{ background: color }}>
        <div className="flex items-center gap-2.5 text-white">
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
            {humanMode ? <Headset size={18} weight="fill" /> : <AvatarIcon />}
          </div>
          <div>
            <div className="font-semibold text-sm leading-tight">{humanMode ? (supportName || "Kundtjänst") : (agent?.name || "AI-agent")}</div>
            <div className="text-[11px] text-white/80 flex items-center gap-1">
              {humanMode
                ? <><span className="h-1.5 w-1.5 rounded-full bg-green-300 inline-block" /> Online · Kundtjänst</>
                : "Online · AI-assistent"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {surveyEnabled && hasUserMessage && !surveyDone && (
            <button data-testid="chat-end-button" onClick={() => setSurveyOpen(true)}
              className="inline-flex items-center gap-1.5 text-white/90 hover:text-white text-xs font-semibold bg-white/15 hover:bg-white/25 rounded-full px-2.5 py-1.5 transition-colors" title="Avsluta chatten">
              <SignOut size={14} weight="bold" /> Avsluta
            </button>
          )}
          <button data-testid="chat-reset-button" onClick={reset} className="text-white/80 hover:text-white p-1" title="Återställ">
            <ArrowsClockwise size={18} weight="bold" />
          </button>
        </div>
      </div>

      {showDisclosure && !humanMode && (
        <div data-testid="ai-disclosure" className="flex items-start gap-2 px-4 py-2 bg-blue-50/70 border-b border-blue-100 text-[11px] leading-snug text-zinc-500">
          <Info size={14} weight="fill" className="text-[#0033FF]/60 mt-0.5 shrink-0" />
          <span>{disclosureText}</span>
        </div>
      )}

      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto scroll-thin p-4 space-y-3 bg-[#FAFAFA]">
        {messages.map((m, i) => {
          const showFeedback = m.role === "assistant" && i > 0 && m.content && !(busy && i === messages.length - 1);
          return (
          <div key={i}>
            {m.role === "assistant" && m.steps && m.steps.some((s) => s.type === "action" || s.type === "observation") && (
              <AgentTimeline steps={m.steps} live={busy && i === messages.length - 1} />
            )}
            <div className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "assistant" && (
              <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 overflow-hidden" style={{ background: `${color}1A`, color }}>
                <AvatarIcon size={15} />
              </div>
            )}
            {m.role === "agent" && (
              <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-emerald-600 bg-emerald-100">
                <Headset size={15} weight="fill" />
              </div>
            )}
            <div
              className={`max-w-[80%] text-sm px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap break-words ${
                m.role === "user" ? "rounded-br-sm text-white"
                  : m.role === "agent" ? "rounded-bl-sm bg-emerald-50 border border-emerald-200 text-emerald-900"
                  : "rounded-bl-sm bg-white border border-zinc-200 text-zinc-800"
              }`}
              style={m.role === "user" ? { background: color } : {}}
            >
              {m.image && (
                <img src={m.image} alt="bifogad" data-testid="chat-image-thumb" className="mb-1.5 rounded-lg max-h-40 w-auto object-cover border border-white/30" />
              )}
              {m.file && (
                <div data-testid="chat-file-chip" className={`mb-1.5 flex w-fit max-w-full items-center gap-1.5 rounded-lg px-2 py-1 text-xs ${m.role === "user" ? "bg-white/20" : "bg-zinc-100 text-zinc-600"}`}>
                  <FileText size={14} weight="fill" className="shrink-0" /> <span className="truncate">{m.file}</span>
                </div>
              )}
              {m.content || (busy && i === messages.length - 1 ? (
                <span className="inline-flex gap-1 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" />
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.4s" }} />
                </span>
              ) : "")}
            </div>
            {m.role === "user" && (
              <div className="h-7 w-7 rounded-full bg-zinc-200 flex items-center justify-center shrink-0 mt-0.5 text-zinc-500">
                <UserCircle size={16} weight="fill" />
              </div>
            )}
            </div>
            {m.role === "assistant" && ((m.sources && m.sources.length) || (m.actions && m.actions.length)) ? (
              <div className="flex flex-wrap gap-1.5 pl-9 mt-1.5">
                {(m.actions || []).map((a, k) => (
                  <span key={`a${k}`} data-testid="chat-action-chip" className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                    <CheckCircle size={12} weight="fill" /> {a.tool}
                  </span>
                ))}
                {(m.sources || []).map((s, k) => (
                  s.url ? (
                    <a key={`s${k}`} href={s.url} target="_blank" rel="noreferrer" data-testid="chat-source-chip"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200 hover:bg-zinc-200 transition-colors max-w-[180px] truncate">
                      <LinkSimple size={11} weight="bold" /> {s.label}
                    </a>
                  ) : (
                    <span key={`s${k}`} data-testid="chat-source-chip"
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 border border-zinc-200 max-w-[180px] truncate">
                      <BookOpen size={11} weight="bold" /> {s.label}
                    </span>
                  )
                ))}
              </div>
            ) : null}
            {showFeedback && (
              <div className="flex justify-start pl-9 mt-1">
                {feedback[i] ? (
                  <span data-testid={`feedback-thanks-${i}`} className="text-[11px] text-zinc-400">Tack för din feedback!</span>
                ) : (
                  <div className="flex items-center gap-1">
                    <button data-testid={`feedback-up-${i}`} onClick={() => sendFeedback(i, "up")} title="Bra svar"
                      className="p-1.5 rounded-md text-zinc-400 hover:text-green-600 hover:bg-green-50 transition-colors">
                      <ThumbsUp size={16} weight="bold" />
                    </button>
                    <button data-testid={`feedback-down-${i}`} onClick={() => sendFeedback(i, "down")} title="Dåligt svar"
                      className="p-1.5 rounded-md text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <ThumbsDown size={16} weight="bold" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
        {agentTyping && (
          <div className="flex gap-2 justify-start" data-testid="agent-typing">
            <div className="h-7 w-7 rounded-full flex items-center justify-center shrink-0 text-emerald-600 bg-emerald-100"><Headset size={15} weight="fill" /></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl rounded-bl-sm px-3.5 py-3 flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: "150ms" }} />
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        {escalated && (
          <div data-testid="chat-escalation-banner" className="flex items-center gap-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-sm">
            <Headset size={18} weight="bold" />
            {humanMode && supportName
              ? `Du är nu kopplad till ${supportName}.`
              : "Vi kopplar dig till en kollega – du får snart svar här."}
          </div>
        )}
      </div>

      <form onSubmit={send} className="p-3 border-t border-zinc-200 bg-white">
        {pendingImage && (
          <div data-testid="chat-image-preview" className="mb-2 inline-flex items-center gap-2 bg-[#FAFAFA] border border-zinc-200 rounded-lg p-1.5 pr-2">
            <img src={pendingImage.dataUrl} alt="" className="h-10 w-10 rounded object-cover" />
            <span className="text-xs text-zinc-500">Bild bifogad</span>
            <button type="button" data-testid="chat-image-remove" onClick={() => setPendingImage(null)} className="text-zinc-400 hover:text-red-500">
              <X size={15} weight="bold" />
            </button>
          </div>
        )}
        {pendingFile && (
          <div data-testid="chat-file-preview" className="mb-2 inline-flex items-center gap-2 bg-[#FAFAFA] border border-zinc-200 rounded-lg p-1.5 pr-2 max-w-full">
            <span className="h-10 w-10 rounded flex items-center justify-center shrink-0" style={{ background: `${color}1A`, color }}>
              <FileText size={20} weight="fill" />
            </span>
            <span className="text-xs text-zinc-600 truncate max-w-[180px]" title={pendingFile.name}>{pendingFile.name}</span>
            <button type="button" data-testid="chat-file-remove" onClick={() => setPendingFile(null)} className="text-zinc-400 hover:text-red-500 shrink-0">
              <X size={15} weight="bold" />
            </button>
          </div>
        )}
        <div className="flex items-center gap-2">
          {(visionEnabled || fileUploadsEnabled) && (
            <>
              <input ref={fileRef} type="file" className="hidden" data-testid="chat-image-input" onChange={pickAttachment}
                accept={[visionEnabled ? "image/*" : "", fileUploadsEnabled ? ".pdf,.docx,.txt,.md,.csv,.json,.log" : ""].filter(Boolean).join(",")} />
              <button type="button" data-testid="chat-attach-button" onClick={() => fileRef.current?.click()} disabled={busy || fileBusy}
                className="h-10 w-10 rounded-full flex items-center justify-center text-zinc-500 hover:bg-zinc-100 disabled:opacity-50 shrink-0"
                title={fileUploadsEnabled && visionEnabled ? "Bifoga bild eller fil" : fileUploadsEnabled ? "Bifoga fil" : "Bifoga bild"}>
                {fileBusy ? <CircleNotch size={18} className="animate-spin" /> : <Paperclip size={18} weight="bold" />}
              </button>
            </>
          )}
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              const ws = wsRef.current;
              if (ws && ws.readyState === 1) {
                try { ws.send(JSON.stringify({ type: "typing", on: true })); } catch { /* ignore */ }
                clearTimeout(typingTimerRef.current);
                typingTimerRef.current = setTimeout(() => {
                  try { ws.send(JSON.stringify({ type: "typing", on: false })); } catch { /* ignore */ }
                }, 1800);
              }
            }}
            placeholder="Skriv ditt meddelande…"
            className="flex-1 bg-[#FAFAFA] border border-zinc-200 rounded-full px-4 py-2.5 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none"
          />
          <button data-testid="chat-send-button" type="submit" disabled={busy || fileBusy || (!input.trim() && !pendingImage && !pendingFile)}
            className="h-10 w-10 rounded-full flex items-center justify-center text-white disabled:opacity-50 shrink-0"
            style={{ background: color }}>
            <PaperPlaneRight size={18} weight="fill" />
          </button>
        </div>
      </form>

      {accessProtected && !accessOk && (
        <div data-testid="access-gate" className="absolute inset-0 z-30 bg-white/97 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center">
          <div className="w-full max-w-xs">
            <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center" style={{ background: `${color}1A`, color }}>
              <LockKey size={28} weight="fill" />
            </div>
            <h3 className="font-display font-bold text-lg text-zinc-800 mt-4">Skyddad assistent</h3>
            <p className="text-sm text-zinc-500 mt-1">Den här assistenten är endast för behörig personal. Ange åtkomstkoden för att fortsätta.</p>
            <input data-testid="access-code-input" type="password" value={accessInput}
              onChange={(e) => setAccessInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlockAccess()}
              placeholder="Åtkomstkod" autoFocus
              className="w-full mt-5 text-center tracking-widest bg-[#FAFAFA] border border-zinc-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />
            {accessError && <p data-testid="access-error" className="text-xs text-red-500 mt-2">{accessError}</p>}
            <button data-testid="access-unlock" onClick={unlockAccess} disabled={!accessInput.trim()}
              className="w-full mt-4 rounded-full py-2.5 font-semibold text-white text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
              style={{ background: color }}>
              <LockKey size={15} weight="fill" /> Lås upp
            </button>
          </div>
        </div>
      )}

      {surveyOpen && (
        <div data-testid="csat-survey-overlay" className="absolute inset-0 z-20 bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center animate-in fade-in duration-200">
          {!surveyDone ? (
            <div className="w-full max-w-sm">
              <button data-testid="survey-close" onClick={() => setSurveyOpen(false)}
                className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-600 p-1"><X size={20} weight="bold" /></button>
              <h3 className="font-display font-bold text-lg text-zinc-800">Hur var din upplevelse?</h3>
              <p className="text-sm text-zinc-500 mt-1">Ditt betyg hjälper oss att bli bättre.</p>

              <div className="flex justify-center gap-1.5 mt-5" data-testid="survey-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} data-testid={`survey-star-${n}`} onClick={() => setSurveyCsat(n)}
                    className="p-1 transition-transform hover:scale-110" title={`${n} av 5`}>
                    <Star size={32} weight={n <= surveyCsat ? "fill" : "regular"}
                      style={{ color: n <= surveyCsat ? color : "#d4d4d8" }} />
                  </button>
                ))}
              </div>

              {npsEnabled && (
                <div className="mt-6" data-testid="survey-nps">
                  <p className="text-xs font-semibold uppercase tracking-wide text-zinc-400">Hur troligt är det att du rekommenderar oss? (0–10)</p>
                  <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                      <button key={n} data-testid={`survey-nps-${n}`} onClick={() => setSurveyNps(n)}
                        className={`h-8 w-8 rounded-md text-sm font-semibold border transition-colors ${surveyNps === n ? "text-white border-transparent" : "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400"}`}
                        style={surveyNps === n ? { background: color } : {}}>{n}</button>
                    ))}
                  </div>
                </div>
              )}

              <textarea data-testid="survey-comment" value={surveyComment} onChange={(e) => setSurveyComment(e.target.value)}
                rows={2} maxLength={1000} placeholder="Något du vill berätta? (valfritt)"
                className="w-full mt-5 bg-[#FAFAFA] border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0033FF]/20 focus:border-[#0033FF] outline-none" />

              <button data-testid="survey-submit" onClick={submitSurvey} disabled={surveyBusy || (!surveyCsat && surveyNps === null)}
                className="w-full mt-4 rounded-full py-2.5 font-semibold text-white text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                style={{ background: color }}>
                {surveyBusy ? <CircleNotch size={16} className="animate-spin" /> : null} Skicka betyg
              </button>
              <button data-testid="survey-skip" onClick={() => setSurveyOpen(false)}
                className="mt-2 text-xs text-zinc-400 hover:text-zinc-600">Hoppa över</button>
            </div>
          ) : (
            <div className="w-full max-w-sm" data-testid="survey-thanks">
              <div className="mx-auto h-14 w-14 rounded-full flex items-center justify-center" style={{ background: `${color}1A`, color }}>
                <CheckCircle size={32} weight="fill" />
              </div>
              <h3 className="font-display font-bold text-lg text-zinc-800 mt-4">Tack för din feedback!</h3>
              <p className="text-sm text-zinc-500 mt-1">Vi uppskattar att du tog dig tid.</p>
              <button data-testid="survey-new-chat" onClick={reset}
                className="mt-5 rounded-full py-2.5 px-6 font-semibold text-white text-sm inline-flex items-center justify-center gap-2"
                style={{ background: color }}>Ny chatt</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function summarizeResult(r) {
  if (r == null) return "";
  if (typeof r === "string") return r;
  try {
    return Object.entries(r).map(([k, v]) => `${k}: ${v}`).join(" · ");
  } catch {
    return "";
  }
}

function AgentTimeline({ steps, live }) {
  return (
    <div data-testid="agent-timeline" className="mb-2 ml-9 rounded-xl border border-zinc-200 bg-white/70 backdrop-blur-sm overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-zinc-100 bg-zinc-50/80">
        {live ? <CircleNotch size={13} weight="bold" className="text-[#0033FF] animate-spin" /> : <Lightning size={13} weight="fill" className="text-[#0033FF]" />}
        <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">{live ? "AI arbetar…" : "AI löste detta autonomt"}</span>
      </div>
      <div className="p-2.5 space-y-1.5">
        {steps.map((s, k) => {
          if (s.type === "think") {
            return (
              <div key={k} data-testid="timeline-think" className="flex items-start gap-2 text-[12px] text-zinc-500 italic">
                <Brain size={14} weight="bold" className="text-violet-500 mt-0.5 shrink-0" />
                <span>{s.text}</span>
              </div>
            );
          }
          if (s.type === "action") {
            const args = s.args ? Object.values(s.args).filter(Boolean).join(", ") : "";
            return (
              <div key={k} data-testid="timeline-action" className="flex items-start gap-2 text-[12px]">
                <Lightning size={14} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
                <span className="text-zinc-700"><b className="font-semibold">{s.label || s.tool}</b>{args ? <span className="text-zinc-400"> — {args}</span> : null}</span>
              </div>
            );
          }
          // observation
          return (
            <div key={k} data-testid="timeline-observation" className="flex items-start gap-2 text-[12px] pl-5">
              <CheckCircle size={13} weight="fill" className={s.ok ? "text-green-500 mt-0.5 shrink-0" : "text-red-500 mt-0.5 shrink-0"} />
              <span className="text-zinc-500">{summarizeResult(s.result)}{s.simulated ? <span className="ml-1 text-[10px] text-zinc-400">(demo)</span> : null}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ChatPanel;
